import React, { useEffect, useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Box, Stack, Typography } from '@mui/material';
import to from 'await-to-js';
import { Alert } from 'c-components';
import apiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import type { CampaignAllowedVenue } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';

type Props = {
    campaignId: number;
    /** Bumped by the container after an upload, so the list reloads. */
    reloadToken: number;
};

/**
 * UL-7912 -- the campaign's allowed venue list: the media plan, as public ids.
 *
 * A play on a venue not on this list is UNBOOKED. With no list at all every screen is treated as
 * booked, which is worth saying plainly rather than showing an empty grid.
 */
const AllowedVenuesTable: React.FC<Props> = ({ campaignId, reloadToken }) => {
    const t = useCommonTranslation();
    const { start, data, isLoading, error } = useAPIClientRequest(
        apiClient.Entities.Campaign.allowedVenues,
    );

    useEffect(() => {
        to(start(campaignId));
    }, [campaignId, reloadToken, start]);

    const rows: CampaignAllowedVenue[] = data?.data?.data ?? [];

    const columns = useMemo<GridColDef<CampaignAllowedVenue>[]>(
        () => [
            {
                field: 'public_id',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.venues.columns.publicId',
                ),
                flex: 1.5,
                minWidth: 180,
            },
            {
                field: 'network_id',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.venues.columns.networkId',
                ),
                flex: 1.5,
                minWidth: 180,
            },
            {
                field: 'partner_venue_id',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.venues.columns.partnerVenueId',
                ),
                flex: 1,
                minWidth: 120,
            },
            {
                field: 'venue_name',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.venues.columns.venueName',
                ),
                flex: 2,
                minWidth: 180,
            },
            {
                field: 'venue_type',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.venues.columns.venueType',
                ),
                flex: 1,
                minWidth: 120,
            },
        ],
        [t],
    );

    if (!isLoading && !error && rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" py={2}>
                {t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.empty')}
            </Typography>
        );
    }

    return (
        <Stack gap={2} sx={{ height: '100%' }}>
            {error && <Alert severity="error">{String(error)}</Alert>}
            <Box sx={{ flex: 1, minHeight: 360 }}>
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    density="compact"
                    pagination
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                />
            </Box>
        </Stack>
    );
};

export default AllowedVenuesTable;
